import clsx from "clsx";
import { useMemo, useState } from "react";

import styles from "./index.module.scss";
import { PassportInputCompact } from "./PassportInputCompact";
import { PhoneInputCompact } from "./PhoneInputCompact";

/** Автозаполнение браузера по коду поля; телефон — в PhoneInputCompact. */
const AUTOCOMPLETE_BY_CODE: Record<string, string> = {
  name: "name",
  fio: "name",
  email: "email",
};

const INTERACTIVE_CODES = new Set([
  "name",
  "fio",
  "phone",
  "email",
  "passport",
]);

export function digitsOnly(value: string) {
  return (value ?? "").replace(/\D/g, "");
}

function hasRunOfSameDigits(digits: string, runLength: number) {
  if (!digits) return false;
  let run = 1;
  for (let i = 1; i < digits.length; i += 1) {
    if (digits[i] === digits[i - 1]) {
      run += 1;
      if (run >= runLength) return true;
    } else {
      run = 1;
    }
  }
  return false;
}

export function isPhoneValid(value: string) {
  const digits = digitsOnly(value);
  if (digits.length !== 11) return false;

  const userDigits = digits.startsWith("7") ? digits.slice(1) : digits;
  if (hasRunOfSameDigits(userDigits, 8)) return false;

  return true;
}

function isEmailFormatValid(value: string) {
  const v = (value ?? "").trim();
  if (!v) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function isPassportValid(value: string) {
  return digitsOnly(value).length === 9;
}

function isFioValid(value: string): boolean {
  const words = (value ?? "").trim().split(/\s+/).filter(Boolean);

  if (words.length !== 3) {
    return false;
  }

  return words.every((w) => w.length >= 2);
}

function isFieldValid(code: string, value: string) {
  if (code === "phone") return isPhoneValid(value ?? "");
  if (code === "name") return (value ?? "").trim().length > 0;
  if (code === "fio") return isFioValid(value ?? "");
  if (code === "email") return isEmailFormatValid(value ?? "");
  if (code === "passport") return isPassportValid(value ?? "");
  return true;
}

function shouldMarkInvalidOnBlur(code: string, value: string) {
  if (code === "phone") return !isPhoneValid(value ?? "");
  if (code === "name") return (value ?? "").trim().length === 0;
  if (code === "fio") return !isFioValid(value ?? "");
  if (code === "email") {
    const t = (value ?? "").trim();
    if (!t) return false;
    return !isEmailFormatValid(value ?? "");
  }
  if (code === "passport") {
    const d = digitsOnly(value ?? "");
    if (!d) return false;
    return !isPassportValid(value ?? "");
  }
  return false;
}

function SuccessCheckIcon() {
  return (
    <svg
      width="10"
      height="8"
      viewBox="0 0 10 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.37458 0.158792C9.63208 0.388692 9.65442 0.783784 9.42458 1.04127L3.47217 7.70792C3.35358 7.84075 3.184 7.91667 3.00596 7.91667C2.82792 7.91667 2.65832 7.84075 2.53975 7.70792L0.158792 5.04125C-0.0711 4.78375 -0.0487331 4.38867 0.20875 4.15883C0.466225 3.92892 0.861325 3.95125 1.09122 4.20875L3.00596 6.35325L8.49217 0.20875C8.722 -0.0487331 9.11708 -0.0711 9.37458 0.158792Z"
        fill="#229317"
      />
    </svg>
  );
}

function ErrorAlertIcon() {
  return (
    <svg
      width="6"
      height="10"
      viewBox="0 0 2 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0 7.64677C0 8.13637 0.3968 8.53336 0.8868 8.53336C1.3768 8.53336 1.7736 8.13657 1.7736 7.64677C1.7736 7.15657 1.3768 6.75977 0.8868 6.75977C0.3968 6.75977 0 7.15657 0 7.64677Z"
        fill="#FF584C"
      />
      <path
        d="M1.34736 5V0.4C1.34736 0.1792 1.16816 0 0.947363 0C0.726563 0 0.547363 0.1792 0.547363 0.4V5C0.547363 5.2208 0.726563 5.4 0.947363 5.4C1.16816 5.4 1.34736 5.2208 1.34736 5Z"
        fill="#FF584C"
      />
    </svg>
  );
}

export default function InputNew({
  label,
  code = "",
  value,
  after,
  right,
  placeholder,
  required = false,
  isMultiple,
  onChange = () => {},
  onBlur = () => {},
  onClick,
  isError,
  errorText = "",
  disabled,
  classProp,
}: any) {
  const interactive = INTERACTIVE_CODES.has(code);

  /** После blur показываем локальную ошибку, если значение не проходит проверку (сама «ошибка» считается из value). */
  const [blurCommitted, setBlurCommitted] = useState(false);

  const localInvalid = useMemo(
    () => blurCommitted && shouldMarkInvalidOnBlur(code, value ?? ""),
    [blurCommitted, code, value],
  );

  const showError = Boolean(isError) || (interactive && localInvalid);
  const valid = isFieldValid(code, value ?? "");
  const showSuccess =
    interactive &&
    !showError &&
    valid &&
    (code !== "email" || (value ?? "").trim().length > 0);

  const handleChange = (next: string) => {
    if (interactive) {
      // While user is typing, hide validation errors. Show them only after blur.
      setBlurCommitted(false);
    }
    onChange(next);
  };

  const handleBlur = (next: string) => {
    if (interactive) {
      setBlurCommitted(true);
    }
    onBlur(next);
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div
      onClick={onClick}
      className={clsx(
        styles.Input,
        classProp,
        disabled && styles.Disabled,
        showError && styles.Error,
        showSuccess && styles.Success,
        code === "phone" && styles.Phone,
        label?.includes("roistat") && styles.Roistat,
      )}
    >
      {label && (
        // eslint-disable-next-line jsx-a11y/label-has-associated-control
        <label htmlFor={code}>
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </label>
      )}
      <div className={styles.Content}>
        {code === "phone" && (
          <span className={styles.Rus}>
            <svg
              width="20"
              height="15"
              viewBox="0 0 20 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <mask
                id="mask0_139_11211"
                maskUnits="userSpaceOnUse"
                x="-7"
                y="-8"
                width="34"
                height="30"
              >
                <path
                  d="M18.5716 0H1.42871C0.738355 0 0.178711 0.559644 0.178711 1.25V12.6786C0.178711 13.3689 0.738355 13.9286 1.42871 13.9286H18.5716C19.2619 13.9286 19.8216 13.3689 19.8216 12.6786V1.25C19.8216 0.559644 19.2619 0 18.5716 0Z"
                  fill="white"
                  stroke="white"
                  strokeWidth="14.2857"
                />
              </mask>
              <g mask="url(#mask0_139_11211)">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0 9.34586H20V4.58398H0V9.34586Z"
                  fill="#0C47B7"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0 14.1076H20V9.3457H0V14.1076Z"
                  fill="#E53B35"
                />
              </g>
            </svg>
          </span>
        )}
        {disabled && <div>{value}</div>}
        {!disabled && isMultiple && (
          <textarea
            onChange={(event) => handleChange(event.target.value)}
            onBlur={(event) => handleBlur(event.target.value)}
            placeholder={placeholder}
            data-test={`${code}Field`}
          >
            {value}
          </textarea>
        )}
        {!disabled &&
          !isMultiple &&
          code !== "phone" &&
          code !== "passport" && (
            <input
              id={code}
              data-test={`${code}Field`}
              placeholder={placeholder}
              autoComplete={AUTOCOMPLETE_BY_CODE[code]}
              onChange={(event) => handleChange(event.target.value)}
              onBlur={(event) => handleBlur(event.target.value)}
              value={value}
            />
          )}
        {!disabled && !isMultiple && code === "passport" && (
          <PassportInputCompact
            id={code}
            dataTest={`${code}Field`}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        )}
        {!disabled && !isMultiple && code === "phone" && (
          <PhoneInputCompact
            id={code}
            dataTest={`${code}Field`}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        )}
        {/* eslint-disable-next-line no-nested-ternary */}
        {showSuccess ? (
          <span className={styles.Right}>
            <span className={styles.SuccessBadge}>
              <SuccessCheckIcon />
            </span>
          </span>
        ) : showError ? (
          <span className={styles.Right}>
            <span className={styles.ErrorBadge}>
              <ErrorAlertIcon />
            </span>
          </span>
        ) : (
          right && <span className={styles.Right}>{right}</span>
        )}
      </div>
      {showError && errorText && (
        <span className={styles.ErrorText}>
          {errorText ?? "Заполните поле"}
        </span>
      )}
      {after && <span className={styles.After}>{after}</span>}
    </div>
  );
}
